import React, { useEffect, useMemo, useState } from 'react';
import {
  Keyboard,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';

import {
  formatAlertHour,
  intentFromCategorySlug,
  SPEND_ALERT_DOW,
  SPEND_ALERT_HOURS,
  SPEND_ALERT_INTENT_PRESETS,
  SPEND_ALERT_LEAD_OPTIONS,
} from '../../config/spendAlertIntents';
import { useAuth } from '../../contexts/AuthContext';
import { colors, radii, spacing, typography } from '../../core/theme/tokens';
import { ApiError } from '../../services/apiClient';
import { putNotificationPreferences } from '../../services/notificationPreferencesService';
import {
  createSpendAlert,
  patchSpendAlert,
  testSpendAlert,
  type ChannelReadiness,
  type SpendAlert,
  type SpendAlertChannels,
  type SpendAlertIntent,
  type SpendAlertSuggestion,
} from '../../services/spendAlertService';
import { notifySpendAlertsChanged } from '../../services/spendAlertsBus';
import { telemetry } from '../../services/telemetry';
import { KeyboardAvoidingContainer } from '../layout/KeyboardAvoidingContainer';
import { dismissKeyboardThen } from '../layout/keyboard';
import { GlassButton } from '../ui/GlassButton';

type ChannelKey = keyof SpendAlertChannels;

type Props = {
  visible: boolean;
  onClose: () => void;
  /** Prefill from Daily spend chip / suggestion. */
  seed?: {
    categorySlug?: string;
    categoryLabel?: string;
    intent?: SpendAlertIntent;
    dayOfWeek?: number;
    hourLocal?: number;
    leadHours?: number;
    avgAmount?: number;
    sampleCount?: number;
    reason?: string;
  };
  /** Edit an existing alert. */
  editing?: SpendAlert | null;
  channels?: ChannelReadiness | null;
  onSaved?: () => void;
};

/**
 * Full set/edit flow: intent → weekday + hour + lead → channels.
 */
export function SetSpendAlertModal({
  visible,
  onClose,
  seed,
  editing = null,
  channels: channelsProp,
  onSaved,
}: Props) {
  const { refreshUser, user } = useAuth();
  const [intent, setIntent] = useState<SpendAlertIntent>('general');
  const [dayOfWeek, setDayOfWeek] = useState(new Date().getDay());
  const [hourLocal, setHourLocal] = useState(18);
  const [leadHours, setLeadHours] = useState(3);
  const [chans, setChans] = useState<SpendAlertChannels>({
    push: true,
    email: false,
    sms: false,
  });
  const [emailDraft, setEmailDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testNote, setTestNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) return;
    setError(null);
    setTestNote(null);
    if (editing) {
      setIntent(editing.intent ?? intentFromCategorySlug(editing.categorySlug));
      setDayOfWeek(editing.dayOfWeek);
      setHourLocal(editing.hourLocal);
      setLeadHours(editing.leadHours);
      setChans({ ...editing.channels, sms: false });
    } else {
      const preset =
        SPEND_ALERT_INTENT_PRESETS.find((p) => p.id === seed?.intent) ??
        SPEND_ALERT_INTENT_PRESETS.find(
          (p) => p.categorySlug === seed?.categorySlug && p.id !== 'general',
        );
      setIntent(
        seed?.intent ??
          preset?.id ??
          intentFromCategorySlug(seed?.categorySlug ?? 'all'),
      );
      setDayOfWeek(seed?.dayOfWeek ?? new Date().getDay());
      setHourLocal(seed?.hourLocal ?? 18);
      setLeadHours(seed?.leadHours ?? 3);
      setChans({
        push: true,
        email: Boolean(channelsProp?.email?.value || user?.email),
        sms: false,
      });
    }
    setEmailDraft(channelsProp?.email?.value ?? user?.email ?? '');
  }, [visible, editing, seed, channelsProp, user?.email]);

  const preset = useMemo(
    () =>
      SPEND_ALERT_INTENT_PRESETS.find((p) => p.id === intent) ??
      SPEND_ALERT_INTENT_PRESETS[SPEND_ALERT_INTENT_PRESETS.length - 1]!,
    [intent],
  );

  const categorySlug =
    editing?.categorySlug ??
    (seed?.categorySlug && seed.categorySlug !== 'all'
      ? seed.categorySlug
      : preset.categorySlug === 'all'
        ? 'dining'
        : preset.categorySlug);
  const categoryLabel =
    editing?.categoryLabel ??
    (seed?.categoryLabel && seed.categorySlug !== 'all'
      ? seed.categoryLabel
      : preset.categoryLabel);

  const preview = useMemo(() => {
    const day = SPEND_ALERT_DOW[dayOfWeek] ?? 'that day';
    return `We’ll nudge you ~${leadHours}h before ${formatAlertHour(hourLocal)} on ${day}s — coaching for “${preset.label}”.`;
  }, [dayOfWeek, hourLocal, leadHours, preset.label]);

  const toggleChannel = (key: ChannelKey) => {
    setChans((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const sendTest = async () => {
    if (!editing?.id) return;
    setTesting(true);
    setError(null);
    setTestNote(null);
    try {
      const res = await testSpendAlert(editing.id, true);
      const lines = (['push', 'email'] as const).map((key) => {
        const ch = res.delivery[key];
        const label = key === 'push' ? 'Push' : 'Email';
        return `${label}: ${ch.sent ? 'sent' : 'not sent'} — ${ch.detail}`;
      });
      setTestNote(lines.join('\n'));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not send test alert');
    } finally {
      setTesting(false);
    }
  };

  const save = async () => {
    if (!chans.push && !chans.email) {
      setError('Pick at least one channel: Push or Email.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const needsEmail = chans.email && !channelsProp?.email?.value && !emailDraft.trim();
      if (needsEmail) {
        setError('Add an email for email alerts.');
        return;
      }

      const emailAddress =
        chans.email && !channelsProp?.email?.value ? emailDraft.trim() : undefined;

      await putNotificationPreferences({
        channels: { push: chans.push, email: chans.email, sms: false },
        types: { spend_alerts: true },
        ...(emailAddress ? { emailAddress } : {}),
      });
      await refreshUser();

      const reason =
        seed?.reason ??
        `Heads-up for ${categoryLabel} on ${SPEND_ALERT_DOW[dayOfWeek]}s around ${formatAlertHour(hourLocal)} — goal: ${preset.label.toLowerCase()}.`;

      if (editing) {
        await patchSpendAlert(editing.id, {
          enabled: true,
          intent,
          categorySlug,
          categoryLabel,
          dayOfWeek,
          hourLocal,
          leadHours,
          timezoneOffsetMinutes: new Date().getTimezoneOffset(),
          channels: { ...chans, sms: false },
          reason,
        });
      } else {
        await createSpendAlert({
          categorySlug,
          categoryLabel,
          intent,
          dayOfWeek,
          hourLocal,
          timezoneOffsetMinutes: new Date().getTimezoneOffset(),
          leadHours,
          channels: { ...chans, sms: false },
          reason,
          avgAmount: seed?.avgAmount,
          sampleCount: seed?.sampleCount,
        });
      }
      void telemetry.logFeature('spend_alert_save', {
        action: editing ? 'update' : 'create',
      });
      notifySpendAlertsChanged();
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save alert');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <KeyboardAvoidingContainer>
        <View style={styles.overlay}>
          <Pressable
            style={styles.backdrop}
            onPress={() => dismissKeyboardThen(onClose)}
            accessibilityLabel="Dismiss"
          />
          <View style={styles.sheet}>
            <ScrollView
              contentContainerStyle={styles.scroll}
              keyboardShouldPersistTaps="handled"
              keyboardDismissMode="interactive"
            >
            <Text style={styles.kicker}>{editing ? 'Update alert' : 'Set spend alert'}</Text>
            <Text style={styles.title}>What’s the goal?</Text>
            <Text style={styles.hint}>
              Tied to Daily spend patterns — we coach you before the usual window.
            </Text>

            <View style={styles.intentGrid}>
              {SPEND_ALERT_INTENT_PRESETS.map((p) => (
                <Pressable
                  key={p.id}
                  onPress={() => setIntent(p.id)}
                  style={[styles.intentCard, intent === p.id && styles.intentCardOn]}
                  accessibilityRole="button"
                  accessibilityState={{ selected: intent === p.id }}
                >
                  <Text style={[styles.intentLabel, intent === p.id && styles.intentLabelOn]}>
                    {p.label}
                  </Text>
                  <Text style={styles.intentBlurb} numberOfLines={2}>
                    {p.blurb}
                  </Text>
                </Pressable>
              ))}
            </View>

            <Text style={styles.label}>Day</Text>
            <View style={styles.row}>
              {SPEND_ALERT_DOW.map((d, i) => (
                <Pressable
                  key={d}
                  onPress={() => setDayOfWeek(i)}
                  style={[styles.chip, dayOfWeek === i && styles.chipOn]}
                  accessibilityRole="button"
                  accessibilityState={{ selected: dayOfWeek === i }}
                >
                  <Text style={[styles.chipText, dayOfWeek === i && styles.chipTextOn]}>{d}</Text>
                </Pressable>
              ))}
            </View>

            <Text style={styles.label}>Typical spend time</Text>
            <View style={styles.row}>
              {SPEND_ALERT_HOURS.map((h) => (
                <Pressable
                  key={h}
                  onPress={() => setHourLocal(h)}
                  style={[styles.chip, hourLocal === h && styles.chipOn]}
                  accessibilityRole="button"
                  accessibilityState={{ selected: hourLocal === h }}
                >
                  <Text style={[styles.chipText, hourLocal === h && styles.chipTextOn]}>
                    {formatAlertHour(h)}
                  </Text>
                </Pressable>
              ))}
            </View>

            <Text style={styles.label}>Warn me</Text>
            <View style={styles.row}>
              {SPEND_ALERT_LEAD_OPTIONS.map((h) => (
                <Pressable
                  key={h}
                  onPress={() => setLeadHours(h)}
                  style={[styles.chip, leadHours === h && styles.chipOn]}
                  accessibilityRole="button"
                  accessibilityState={{ selected: leadHours === h }}
                >
                  <Text style={[styles.chipText, leadHours === h && styles.chipTextOn]}>
                    {h}h before
                  </Text>
                </Pressable>
              ))}
            </View>

            <Text style={styles.label}>Channels</Text>
            <View style={styles.row}>
              {(['push', 'email'] as ChannelKey[]).map((c) => (
                <Pressable
                  key={c}
                  onPress={() => toggleChannel(c)}
                  style={[styles.chip, chans[c] && styles.chipOn]}
                  accessibilityRole="button"
                  accessibilityState={{ selected: chans[c] }}
                >
                  <Text style={[styles.chipText, chans[c] && styles.chipTextOn]}>
                    {c === 'push' ? 'Push' : 'Email'}
                  </Text>
                </Pressable>
              ))}
            </View>

            {chans.email && !channelsProp?.email?.value ? (
              <View style={styles.contactBox}>
                <TextInput
                  value={emailDraft}
                  onChangeText={setEmailDraft}
                  placeholder="Email for alerts"
                  placeholderTextColor={colors.textMuted}
                  keyboardType="email-address"
                  autoCapitalize="none"
                  returnKeyType="done"
                  blurOnSubmit
                  onSubmitEditing={() => Keyboard.dismiss()}
                  style={styles.input}
                />
              </View>
            ) : null}

            <Text style={styles.preview}>{preview}</Text>
            {error ? <Text style={styles.error}>{error}</Text> : null}
            {testNote ? <Text style={styles.testNote}>{testNote}</Text> : null}

            <View style={styles.actions}>
              <GlassButton variant="secondary" size="sm" onPress={onClose}>
                Cancel
              </GlassButton>
              {editing ? (
                <GlassButton
                  variant="secondary"
                  size="sm"
                  disabled={testing || busy}
                  onPress={() => void sendTest()}
                >
                  {testing ? 'Sending…' : 'Send test now'}
                </GlassButton>
              ) : null}
              <GlassButton
                variant="primary"
                size="sm"
                disabled={busy || testing}
                onPress={() => void save()}
              >
                {busy ? 'Saving…' : editing ? 'Save changes' : 'Save alert'}
              </GlassButton>
            </View>
          </ScrollView>
        </View>
        </View>
      </KeyboardAvoidingContainer>
    </Modal>
  );
}

/** Helper for suggestion → modal seed. */
export type SpendAlertModalSeed = NonNullable<Props['seed']>;

export function seedFromSuggestion(s: SpendAlertSuggestion): SpendAlertModalSeed {
  return {
    categorySlug: s.categorySlug,
    categoryLabel: s.categoryLabel,
    intent: intentFromCategorySlug(s.categorySlug),
    dayOfWeek: s.dayOfWeek,
    hourLocal: s.hourLocal,
    leadHours: s.leadHours,
    avgAmount: s.avgAmount,
    sampleCount: s.sampleCount,
    reason: s.reason,
  };
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'center',
    padding: spacing.lg,
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.55)',
  },
  sheet: {
    maxHeight: '92%',
    flexShrink: 1,
    borderRadius: radii.lg,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.glassBorder,
    backgroundColor: colors.surfaceElevated,
    overflow: 'hidden',
  },
  scroll: {
    padding: spacing.lg,
    gap: spacing.sm,
  },
  kicker: {
    ...typography.caption,
    fontSize: 11,
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    color: colors.accent,
    fontWeight: '700',
  },
  title: {
    ...typography.headline,
    fontSize: 20,
    color: colors.text,
  },
  hint: {
    ...typography.body,
    fontSize: 13,
    color: colors.textMuted,
    lineHeight: 18,
    marginBottom: 4,
  },
  intentGrid: { gap: 8 },
  intentCard: {
    padding: 12,
    borderRadius: radii.md,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.glassBorder,
    backgroundColor: 'rgba(255,255,255,0.03)',
    gap: 2,
  },
  intentCardOn: {
    borderColor: 'rgba(61, 214, 164, 0.45)',
    backgroundColor: 'rgba(61, 214, 164, 0.1)',
  },
  intentLabel: {
    ...typography.body,
    fontSize: 14,
    fontWeight: '700',
    color: colors.text,
  },
  intentLabelOn: { color: colors.accent },
  intentBlurb: {
    ...typography.caption,
    fontSize: 12,
    color: colors.textMuted,
    lineHeight: 16,
  },
  label: {
    ...typography.caption,
    fontWeight: '700',
    color: colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    marginTop: 6,
  },
  row: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  chip: {
    paddingHorizontal: 10,
    paddingVertical: 7,
    borderRadius: radii.pill,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.glassBorder,
  },
  chipOn: {
    borderColor: 'rgba(61, 214, 164, 0.45)',
    backgroundColor: 'rgba(61, 214, 164, 0.12)',
  },
  chipText: { ...typography.caption, fontWeight: '700', color: colors.textMuted },
  chipTextOn: { color: colors.accent },
  contactBox: { gap: 6 },
  input: {
    ...typography.body,
    fontSize: 13,
    color: colors.text,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.glassBorder,
    borderRadius: radii.sm,
    paddingHorizontal: 10,
    paddingVertical: 8,
    backgroundColor: colors.surfaceElevated,
  },
  preview: {
    ...typography.body,
    fontSize: 12,
    color: colors.textMuted,
    lineHeight: 17,
    marginTop: 4,
  },
  error: { ...typography.caption, color: colors.danger },
  testNote: {
    ...typography.caption,
    fontSize: 12,
    lineHeight: 17,
    color: colors.accent,
  },
  actions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    gap: spacing.sm,
    marginTop: spacing.sm,
  },
});
